import { useMemo } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import {
  Pager,
  RoleBadge,
  Section,
  TableScroller,
  useBearer,
  usePagedQuery,
  useToast,
} from './_admin-utils'

interface RoleHistoryItem {
  id?: string
  changed_at: string
  target_id?: string
  target_email?: string
  old_role?: string
  new_role?: string
  actor_id?: string
  actor_email?: string
  reason?: string
}

interface FetcherParams {
  page: number
  pageSize: number
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export default function RoleHistory() {
  const { profile } = useAuthContext()
  const bearer = useBearer()
  const { ToastHost } = useToast()

  const fetcher = useMemo(
    () =>
      async ({ page, pageSize }: FetcherParams): Promise<{ items: RoleHistoryItem[]; total: number }> => {
        if (!bearer) throw new Error('Not authenticated')
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })
        const response = await fetch(`/api/v1/admin/audit/role-history?${params.toString()}`, {
          headers: { ...bearer },
        })
        const json = await response.json().catch(() => ({})) as { data?: { items?: RoleHistoryItem[]; total?: number }; items?: RoleHistoryItem[]; total?: number; error?: string } | null
        if (!response.ok) {
          throw new Error(json?.error || 'Failed to load role history')
        }
        return {
          items: Array.isArray(json?.data?.items) ? json.data.items : Array.isArray(json?.items) ? json.items : [],
          total: typeof json?.data?.total === 'number' ? json.data.total : typeof json?.total === 'number' ? json.total : 0,
        }
      },
    [bearer],
  )

  const { items, page, pageSize, pageCount, loading, error, load } = usePagedQuery(fetcher, [fetcher])
  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '')

  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const handlePage = (next: number) => {
    if (loading || next === page) return
    load(next)
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Role History</h1>
        <p className="text-sm text-cocoa/70 max-w-2xl">
          Review a chronological record of profile role changes. Entries include both automatic guardrails and manual
          adjustments performed by superadmins.
        </p>
      </header>

      <Section
        title="Role Changes"
        actions={<a href="/portal/admin" className="text-sm underline text-cocoa/70 hover:text-cocoa">← Admin</a>}
      >
        <div className="overflow-hidden rounded-xl border border-white/70">
          <TableScroller minWidth={820}>
            <table className="min-w-full divide-y divide-white/60 text-sm">
              <thead className="bg-shell/60 text-cocoa/70 uppercase tracking-wide text-xs">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Changed</th>
                  <th className="px-4 py-3 font-semibold">Target</th>
                  <th className="px-4 py-3 font-semibold">Old Role</th>
                  <th className="px-4 py-3 font-semibold">New Role</th>
                  <th className="px-4 py-3 font-semibold">Actor</th>
                  <th className="px-4 py-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white/60">
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-cocoa/60" colSpan={6}>
                      Loading history…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-red-600" colSpan={6}>
                      {error}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-cocoa/60" colSpan={6}>
                      No role changes logged yet.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id || `${item.changed_at}-${item.target_id || 'target'}`} className="hover:bg-shell/40">
                      <td className="px-4 py-3 text-cocoa/80">{formatDateTime(item.changed_at)}</td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <div className="font-medium text-cocoa/90">{item.target_email || item.target_id}</div>
                        <div className="text-xs text-cocoa/60">{item.target_id}</div>
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <RoleBadge role={item.old_role} />
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <RoleBadge role={item.new_role} />
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        <div className="font-medium text-cocoa/90">{item.actor_email || item.actor_id || 'System'}</div>
                        {item.actor_id ? <div className="text-xs text-cocoa/60">{item.actor_id}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-cocoa/80">
                        {item.reason || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScroller>
        </div>
        <div className="pt-4">
          <Pager page={page} pageCount={pageCount} onPage={handlePage} />
        </div>
      </Section>

      <ToastHost />
    </div>
  )
}
